import template from 'url-template';
import moment from 'moment';
import axios from 'axios';
import { ConcurrencyManager } from 'axios-concurrency';

import { cloneDeep, get, isEmpty, isNil, isNaN, toNumber,orderBy } from 'lodash';

import {
    CLEAR_ENTITY_SENSOR_DATA,
    CLEAR_SELECTED_NAV_CLIENT,
    CLEAR_SELECTED_NAV_SITE,
    CLEAR_SELECTED_NAV_EQUIPMENT,
    CLEAR_SELECTED_NAV_SYSTEM,
    FETCH_EQUIPMENT_RULES,
    FETCH_FAULT_OVERLAY,
    FETCH_NAV_CLIENTS,
    FETCH_NAV_EQUIPMENT,
    FETCH_NAV_SITES,
    FETCH_SCORING_DATA,
    SET_ACTIVE_NAV_CLIENT,
    SET_ACTIVE_NAV_COMPANY,
    SET_ACTIVE_NAV_SITE,
    SET_ACTIVE_NAV_EQUIPMENT,
    SET_ALL_SENSORS,
    SET_BOB_LOADING,
    SET_CONFIG_SENSORS,
    SET_COMPANY_APPLICATIONS,
    SET_ENTITY_RULES,
    SET_ENTITY_SENSOR_DATA,
    SET_ENTITY_SENSORS,
    SET_ENTITY_SCORES_DATA,
    SET_ENTITY_SCORING_DATA,
    SET_FAULT_DATA,
    SET_NAV_CLIENT,
    SET_NAV_COMPANY,
    SET_NAV_EQUIPMENT,
    SET_NAV_SYSTEM,
    SET_NAV_SITE,
    FETCH_NAV_SYSTEMS,
    FETCH_FAULT_HISTORY,
    FETCH_FAULT_SUMMARY
} from '../actions/actionTypes';

import awsHeader from '../helpers/awsAuth';
import k2aBaseApi from '../apis/key2act';
import lambdaBaseApi from '../apis/lambda';

import { enqueueSnackbar, handleNetworkError } from './applicationMessageActions';
import { setProgressBarLoading } from './coreActions';
import {setDashboardCurrentTimestamp} from './dashboardActions';
import {convertStringToNumber} from '../helpers/numbers';

const ClientsUrl = template.parse('/companies/{companyId}/clients{?limit,page,searchFilter,sort}');
const ClientUrl = template.parse('/clients/{clientId}');
const EquipmentUrl = template.parse('/sites/{siteId}/equipment{?limit,page,searchFilter,sort,extendedData}');
const SitesUrl = template.parse('/clients/{clientId}/sites{?limit,page,searchFilter,sort}');
const SiteUrl = template.parse('/sites/{siteId}');
const SpecificEquipmentUrl = template.parse('/equipment/{equipmentId}{?extendedData}');
const SpecificSensorUrl = template.parse('/sensors/{sensorId}{?deleteRelatedCalcSensors,resetcalculatedsensor}');
const EquipmentSensorDataUrl = template.parse('/sites/{siteId}/sensors{?limit,page,searchFilter,sort,parentId}');
const EntitySensorDataUrl = template.parse('/sensordata{?equipmentId,sensorId,weatherId,systemId,startTimestamp,endTimestamp,page,pageSize,desiredInterval}');
const EntityScoresDataUrl = template.parse('/scoring{?entityId,startTimestamp,endTimestamp,pageSize}');
const EntityFaultsDataUrl = template.parse('/companies/{companyId}/equipment/{entityId}/faults{?StartTime,EndTime,limit,page,sort}');
const ScoringDataUrl = template.parse('/companies/{companyId}/score-sensordata{?From,To,SensorId,EquipmentId,SystemId,DesiredTimeSeriesIntervalInMinutes,Limit,Page}');
const SystemsUrl = template.parse('/sites/{siteId}/systems{?searchFilter,limit,equipmentId}');

const FaultSummaryUrl = template.parse('/companies/{companyId}/equipment/{entityId}/faults-summary{?StartTime,EndTime,limit,page,sort}');
const FaultHistoryUrl = template.parse('/companies/{companyId}/equipment/{entityId}/faults-history{?StartTime,EndTime,RuleId,limit,page,sort}');

const DEFAULT_GRAPH_PAGE_LIMIT = 1000;
const DEFAULT_SENSOR_GRAPH_PAGE_LIMIT = 1000;
const DEFAULT_PAGE_LIMIT = 25;
const DEFAULT_SCORES_PAGE_LIMIT = 1000;
const DEFAULT_SORT = 'name^asc';
const NAV_DEFAULT_PAGE_LIMIT = 250;
const MAX_CONCURRENCY_REQUESTS = 10;

export const fetchNavClients = (companyId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    header.headers['K2A-Company'] = companyId;

    options.limit || (options.limit = NAV_DEFAULT_PAGE_LIMIT);
    options.page || (options.page = 1);
    options.searchFilter || (options.searchFilter = '');
    options.sort || (options.sort = DEFAULT_SORT);

    try {
        const response = await k2aBaseApi.get(
            ClientsUrl.expand({
                companyId,
                ...options
            }),
            header
        );

        let clients = get(response, 'data.data', []);
        const meta = get(response, 'data.meta', {});

        if (options.loadMore) {
            const priorClients = getState().bob.navClients;
            clients = [...priorClients, ...clients];
        }

        dispatch({
            type:    FETCH_NAV_CLIENTS,
            payload: {
                clients,
                meta
            }
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchNavEquipment = (siteId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    header.headers['K2A-Company'] = getState().bob.targetNavCompany.id;

    options.limit || (options.limit = 200);
    options.page || (options.page = 1);
    options.searchFilter || (options.searchFilter = '');
    options.sort || (options.sort = DEFAULT_SORT);
    options.extendedData = true;

    try {
        const response = await k2aBaseApi.get(
            EquipmentUrl.expand({
                siteId,
                ...options
            }),
            header
        );

        let equipment = get(response, 'data.data', []);
        const meta = get(response, 'data.meta', {});

        if (options.loadMore) {
            const priorEquipment = getState().bob.navEquipment;
            equipment = [...priorEquipment, ...equipment];
        }

        dispatch({
            type:    FETCH_NAV_EQUIPMENT,
            payload: {
                equipment,
                meta
            }
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchNavSystems = (siteId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    header.headers['K2A-Company'] = getState().bob.targetNavCompany.id;

    options.limit || (options.limit = DEFAULT_PAGE_LIMIT);
    options.page || (options.page = 1);
    options.searchFilter || (options.searchFilter = '');
    options.sort || (options.sort = DEFAULT_SORT);

    try {
        const response = await k2aBaseApi.get(
            SystemsUrl.expand({
                siteId,
                ...options
            }),
            header
        );

        let system = get(response, 'data.data', []);
        const meta = get(response, 'data.meta', {});

        if (options.loadMore) {
            const priorSystem = getState().bob.navSystems;
            system = [...priorSystem, ...system];
        }

        dispatch({
            type:    FETCH_NAV_SYSTEMS,
            payload: {
                system,
                meta
            }
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchNavSites = (clientId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    header.headers['K2A-Company'] = getState().bob.targetNavCompany.id;

    options.limit || (options.limit = 200);
    options.page || (options.page = 1);
    options.searchFilter || (options.searchFilter = '');
    options.sort || (options.sort = DEFAULT_SORT);

    try {
        const response = await k2aBaseApi.get(
            SitesUrl.expand({
                clientId,
                ...options
            }),
            header
        );

        let sites = get(response, 'data.data', []);
        const meta = get(response, 'data.meta', {});

        if (options.loadMore) {
            const priorSites = getState().bob.navSites;
            sites = [...priorSites, ...sites];
        }

        dispatch({
            type:    FETCH_NAV_SITES,
            payload: {
                sites,
                meta
            }
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchSingleClient = ids => async (dispatch, getState) => {
    const header = await awsHeader();

    const activeCompany = getState().bob.activeNavCompany.id;
    if (activeCompany) {
        header.headers['K2A-Company'] = activeCompany;
    }

    try {
        const response = await k2aBaseApi.get(
            ClientUrl.expand({
                clientId: ids.client
            }),
            header
        );

        const client = get(response, 'data', {});

        dispatch(setNavClient(client));
        dispatch(fetchNavSites(client.id));
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchSingleEquipment = (ids, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    
    const activeCompany = getState().bob.activeNavCompany.id;
    if (activeCompany) {
        header.headers['K2A-Company'] = activeCompany;
    }

    await dispatch(fetchSingleSite(ids));

    try {
        const response = await k2aBaseApi.get(
            SpecificEquipmentUrl.expand({
                equipmentId: ids.equipment,
                ...options
            }),
            header
        );

        const equipment = get(response, 'data', {});

        dispatch(setNavEquipment(equipment));
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchSingleSite = ids => async (dispatch, getState) => {
    const header = await awsHeader();

    const activeCompany = getState().bob.activeNavCompany.id;
    if (activeCompany) {
        header.headers['K2A-Company'] = activeCompany;
    }

    await dispatch(fetchSingleClient(ids));

    try {
        const response = await k2aBaseApi.get(
            SiteUrl.expand({
                siteId: ids.site
            }),
            header
        );

        const site = get(response, 'data', {});

        dispatch(setNavSite(site));
        dispatch(fetchNavEquipment(site.id));
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const deleteSensor = (sensorId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.delete(
            SpecificSensorUrl.expand({
                sensorId,
                ...options
            }),
            header
        );

        dispatch(enqueueSnackbar('Sensor deleted successfully.', 'success'));

        return true;
    } catch (error) {
        if (error.response.data === 'This sensor is related to calculated sensors and cannot be deleted.') {
            return error.response.data;
        }
        return false;
    }
};

export const resetCalculatedSensor = (sensorId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    const body = {};

    try {
        await k2aBaseApi.put(
            SpecificSensorUrl.expand({
                sensorId,
                ...options
            }),
            body,
            header
        );

        dispatch(enqueueSnackbar('Sensor calculation reset successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
    }

};

export const fetchEntitySensors = (siteId, parentId,  options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    if (options.mutateCompanyId) {
        const mutatedCompanyId = getState().bob.targetNavCompany.id;

        header.headers['K2A-Company'] = mutatedCompanyId;
    }
    
    options.limit || (options.limit = 500);
    options.page || (options.page = 1);
    options.searchFilter || (options.searchFilter = '');
    options.sort || (options.sort = DEFAULT_SORT);

    dispatch(setBobLoading('EntitySensors'));

    try {
        const response = await k2aBaseApi.get(
            EquipmentSensorDataUrl.expand({
                siteId,
                parentId,
                ...options
            }),
            header
        );

        let sensors = get(response, 'data.data', []) || [];
        const meta = get(response, 'data.meta', {});
        const rawUnits = getState().architect.rawUnits;

        sensors.forEach(s => {
            if (!isNil(s.linkedSource.agentName) && !isNil(s.linkedSource.deviceName) && !isNil(s.linkedSource.deviceObjectName)) {
                s.linkedSourceText = s.linkedSource.agentName + '/' + s.linkedSource.deviceName + '/' + s.linkedSource.deviceObjectName;
            }
            else if (s.calculatedSensor) {
                s.linkedSourceText = s.sensorTemplateName;
            }
            else {
                s.linkedSourceText = 'Not linked';
            }
            const rawUnit = rawUnits.flatMap(u => u.items).find(ru => ru.id === s.rawUnitId);
            if (rawUnit) {
                s.rawUnitDisplay = rawUnit.displayValue;
            }
            else {
                s.rawUnitDisplay = '-';
            }
        });

        if (options.loadMore) {
            const priorSensors = getState().bob.allSensors;
            sensors = [...priorSensors, ...sensors];
        }

        dispatch(setAllSensors(sensors, meta));

        if (!options.search) {
            dispatch(setEntitySensors(sensors, meta));
        }

        if (isEmpty(getState().bob.configSensors)) dispatch(setConfigSensors(sensors));
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('EntitySensors'));
    }
};

export const fetchConfigSensor = (siteId, parentId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    dispatch(setBobLoading('ConfigSensors'));

    let totalPayload = [];
    let page = 1;

    try {
        const getPayload = async () => {
            options.page = page;
            options.limit = 100;

            const response = await k2aBaseApi.get(
                EquipmentSensorDataUrl.expand({
                    siteId,
                    parentId,
                    ...options
                }),
                header
            );

            const sensors = get(response, 'data.data', []) || [];
            const totalCount = get(response, 'data.meta.totalCount', 0);

            totalPayload = [...totalPayload, ...sensors];
            page++;

            if (sensors.length > 0 && totalPayload.length < totalCount) {
                return await getPayload(page);
            }

            return;

        };

        await getPayload();

        dispatch(setConfigSensors(totalPayload));
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('ConfigSensors'));
    }
};

export const clearEntitySensorData = () => (dispatch, getState) => {
    dispatch({
        type: CLEAR_ENTITY_SENSOR_DATA
    });
};

export const fetchEntitySensorData = (sensors, startTimestamp, endTimestamp, desiredInterval, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    const current = getState().dashboard.dashboardCurrentTimeStamp;

    options.limit || (options.pageSize = DEFAULT_SENSOR_GRAPH_PAGE_LIMIT);
    options.page || (options.page = 1);

    dispatch(setBobLoading('SensorData'));
    dispatch(setBobLoading('SensorCalculations'));
    dispatch(setProgressBarLoading(true));

    // dont pass timestamp when current is selected
    if (getState().dashboard.dashboardIsCurrentTimeStamp) {
        startTimestamp = null;
        endTimestamp = null;
    }

    if (!isEmpty(getState().bob.entitySensorData)) {
        dispatch({
            type: CLEAR_ENTITY_SENSOR_DATA
        });
    }

    let wSensors = [];
    if (!isEmpty(getState().dashboard.sensorConfiguration)) {
        wSensors = getState().dashboard.sensorConfiguration.sensors.filter(item => item.isWeatherSensor);      
    }

    try {
        const manager = ConcurrencyManager(lambdaBaseApi, MAX_CONCURRENCY_REQUESTS);
        let url = EntitySensorDataUrl.expand({ startTimestamp, endTimestamp, desiredInterval, ...options });

        if (!isEmpty(sensors)) {
            sensors.forEach(sensor => {
                url = `${url}&sensorId=${sensor.id}`;
            });
        }

        if (!isEmpty(options.weatherSensors)) {
            options.weatherSensors = options.weatherSensors.replace('maxTemperature', 'tempmax');
            options.weatherSensors = options.weatherSensors.replace('temperature', 'temp');
            options.weatherSensors = options.weatherSensors.replace('minTemperature', 'tempmin');

            url = `${url}&weatherId=${options.weatherSensors}`;
        }

        const response = await lambdaBaseApi.get(
            url,
            header
        );

        const meta = get(response, 'data.meta', []);
        const data = get(response, 'data.data', []);
        const total = meta.totalCount;

        let totalPages = total / options.pageSize;
        let count;
        if (Number.isInteger(totalPages)) {
            count = totalPages;
        }
        else {
            count = totalPages + 1;
        }

        let promises = [];
        for (let i = 2; i <= count; i++) {
            url = url.replace(`page=${i - 1}`, `page=${i}`);
            promises.push(lambdaBaseApi.get(
                url,
                header
            ));
        }

        let results = [];
        await Promise.all(promises.map(p => p))
            .then(axios.spread((...result) => {
                result.forEach(r => {
                    results.push(r.data.data);
                });
            }));

        manager.detach();

        let allSensorData = orderBy(data.concat(...results), 'timestamp', 'asc');

        let sensorData = [];

        if (meta.hasErrors) {
            const messages = meta.messages.join(' ');
            dispatch(enqueueSnackbar(messages, 'error'));
        }
        else {
            let weatherSensors = [];

            if (!isEmpty(options.weatherSensors)) {
                let weather = options.weatherSensors.split(':');
                weather.forEach((item, index) => {
                    if (index > 0) {

                        const weatherSensorId = `${weather[0]}:${item}`;
                        const sensor = allSensorData.find(item => item.weatherId === weatherSensorId);
                        const name = item[0].toUpperCase() + item.slice(1).toLowerCase();

                        if (sensor) {
                            switch (name) {
                                case 'Temp':
                                    sensor.name = 'Temperature';
                                    break;
                                case 'Tempmin':
                                    sensor.name = 'Min Temperature';
                                    break;
                                case 'Tempmax':
                                    sensor.name = 'Max Temperature';
                                    break;
                                default:
                                    sensor.name = name;
                                    break;
                            }

                            weatherSensors.push(sensor);
                        }
                    }
                });
            }

            const combinedSensors = [...sensors, ...weatherSensors];
            combinedSensors.forEach(sensor => {
                let item = {
                    sensorId:           sensor.id ? sensor.id : sensor.weatherId,
                    sensorName:         sensor.name,
                    rawUnitId:          sensor.rawUnitId,
                    presentationUnitId: sensor.presentationUnitId,
                    data:               []
                };
                //Weather Sensors
                if (sensor.isWeather) {
                    item.isWeather = true;

                    if (options.axes) {
                        if (!isEmpty(wSensors)) {
                            let tempSensors = wSensors.filter(item => item.name === sensor.name);
                            if (!isEmpty(tempSensors)) {
                                item.axis = options.axes[tempSensors[0].selectedIndex] ? options.axes[tempSensors[0].selectedIndex] : options.axes[0];
                            } else {
                                item.axis = options.axes[0];
                            }
                        } else {
                            item.axis = options.axes[0];
                        }
                    }

                    allSensorData.forEach(point => {
                        if (point.weatherId === sensor.weatherId) {
                            let convertedValue = null;
                            if (point.convertedValue && point.convertedValue !== '') {
                                if (point.convertedValue?.toString.toString().toLowerCase() === 'pollfailure') {
                                    convertedValue = 'Poll Failure';
                                }
                                else if (point.dataType) {
                                    switch (point.dataType) {
                                        case 'boolean':
                                            convertedValue = point.convertedValue.toLowerCase() === 'true';
                                            break;
                                        case 'float':
                                        case 'integer':
                                            convertedValue = convertStringToNumber(point.convertedValue);
                                            break;
                                        default:
                                            convertedValue = point.convertedValue;
                                            break;
                                    }
                                }
                                else {
                                    convertedValue = point.convertedValue;
                                }
                            }
                            if (getState().dashboard.dashboardIsCurrentTimeStamp) {
                                item.data.push({
                                    timestamp:        new Date(current),
                                    convertedValue:   convertedValue,
                                    dataType:         point.dataType,
                                    presentationUnit: point.presentationUnit,
                                    sampleInterval:   point.sampleInterval
                                });
                            } else {
                                item.data.push({
                                    timestamp:        new Date(point.timestamp),
                                    convertedValue:   convertedValue,
                                    dataType:         point.dataType,
                                    presentationUnit: point.presentationUnit,
                                    sampleInterval:   point.sampleInterval
                                });
                            }
                        }
                    });
                    sensorData.push(item);

                } else {
                    // Normal Sensors
                    if (options.axes) {
                        item.axis = options.axes[sensor.selectedIndex] ? options.axes[sensor.selectedIndex] : options.axes[0];
                    }

                    if (options.entityId) {
                        if (sensor.equipment) {
                            if (options.entityId !== sensor.equipment.id) {
                                item.sensorName = `${sensor.equipment.name}-${item.sensorName}`;
                            }
                        } else if (sensor.system) {
                            if (options.entityId !== sensor.system.id) {
                                item.sensorName = `${sensor.system.name}-${item.sensorName}`;
                            }
                        }
                    }

                    allSensorData.forEach(point => {
                        if (point.sensorId === sensor.id) {
                            let convertedValue = null;
                            if (point.convertedValue && point.convertedValue !== '') {
                                if (point.convertedValue?.toString().toLowerCase() === 'pollfailure') {
                                    convertedValue = 'Poll Failure';
                                }
                                else if (point.dataType) {
                                    switch (point.dataType) {
                                        case 'boolean':
                                            convertedValue = point.convertedValue.toString().toLowerCase() === 'true';
                                            break;
                                        case 'float':
                                        case 'integer':
                                            convertedValue = convertStringToNumber(point.convertedValue);
                                            break;
                                        default:
                                            convertedValue = point.convertedValue;
                                            break;
                                    }
                                }
                                else {
                                    convertedValue = point.convertedValue;
                                }
                            }
                            if (getState().dashboard.dashboardIsCurrentTimeStamp) {
                                item.data.push({
                                    timestamp:        new Date(current),
                                    convertedValue:   convertedValue,
                                    dataType:         point.dataType,
                                    presentationUnit: point.presentationUnit,
                                    sampleInterval:   point.sampleInterval
                                });
                            } else {
                                item.data.push({
                                    timestamp:        new Date(point.timestamp),
                                    convertedValue:   convertedValue,
                                    dataType:         point.dataType,
                                    presentationUnit: point.presentationUnit,
                                    sampleInterval:   point.sampleInterval
                                });
                            }
                        }
                    });
                    sensorData.push(item);                  
                }
            });
        }

        dispatch({
            type:    SET_ENTITY_SENSOR_DATA,
            payload: [...sensorData]
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('SensorData'));
        dispatch(setBobLoading('SensorCalculations'));
        dispatch(setProgressBarLoading(false));
    }
};

export const fetchBulkEntitySensorData = (sensors, startTimestamp, endTimestamp, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    if (!sensors.length) return;

    options.limit || (options.pageSize = DEFAULT_SENSOR_GRAPH_PAGE_LIMIT);
    options.page || (options.page = 1);

    let promises = [];

    for (const sensor of sensors) {
        let sensorId = sensor.id;

        promises.push(
            lambdaBaseApi.get(
                EntitySensorDataUrl.expand({
                    sensorId,
                    startTimestamp,
                    endTimestamp,
                    ...options
                }),
                header
            )
        );
    }

    Promise.all(promises.map(p => p.catch(err => err))).then(results => {
        let sensorData = [];

        results.forEach((result, index) => {
            let sensorName = sensors[index].name;
            let sensorId = sensors[index].id;

            const data = get(result, 'data.data', []);

            let newDataObject = {
                sensorId,
                sensorName,
                data: []
            };

            if (!isNil(data)) {
                data.forEach(item => {
                    newDataObject.data.push({
                        timestamp:      new Date(item.timestamp),
                        convertedValue: item.convertedValue
                    });
                });
            }
            sensorData.push(newDataObject);
        });

        dispatch({
            type:    SET_ENTITY_SENSOR_DATA,
            payload: sensorData
        });
    });
};

export const removeEntitySensorData = sensorId => async (dispatch, getState) => {
    const sensorData = getState().bob.entitySensorData;

    const payload = sensorData.filter(data => data.sensorId !== sensorId);

    dispatch({
        type: SET_ENTITY_SENSOR_DATA,
        payload
    });
};

export const setNavOptions = (target, data, meta) => async dispatch => {
    switch (target) {
        case 'client':
            dispatch({
                type:    FETCH_NAV_CLIENTS,
                payload: {
                    clients: data,
                    meta:    meta
                }
            });
            break;
        case 'site':
            dispatch({
                type:    FETCH_NAV_SITES,
                payload: {
                    sites: data,
                    meta:  meta
                }
            });
            break;
        case 'equipment':
            dispatch({
                type:    FETCH_NAV_EQUIPMENT,
                payload: {
                    equipment: data,
                    meta:      meta
                }
            });
            break;
        default:
            break;
    }
};

export const resetNavOptions = target => async dispatch => {
    switch (target) {
        case 'all':
            dispatch(setNavCompany({}));
            dispatch(setNavClient({}));
            dispatch(setNavSite({}));
            dispatch(setNavEquipment({}));
            dispatch(setNavSystem({}));

            dispatch({
                type:    FETCH_NAV_CLIENTS,
                payload: {
                    clients: [],
                    meta:    {}
                }
            });

            dispatch({
                type:    FETCH_NAV_SITES,
                payload: {
                    sites: [],
                    meta:  {}
                }
            });

            dispatch({
                type:    FETCH_NAV_EQUIPMENT,
                payload: {
                    equipment: [],
                    meta:      {}
                }
            });

            dispatch({
                type:    FETCH_NAV_SYSTEMS,
                payload: {
                    system: [],
                    meta:   {}
                }
            });
            break;
        case 'company':
            dispatch(setNavClient({}));
            dispatch(setNavSite({}));
            dispatch(setNavEquipment({}));
            dispatch(setNavSystem({}));

            dispatch({
                type:    FETCH_NAV_SITES,
                payload: {
                    sites: [],
                    meta:  {}
                }
            });

            dispatch({
                type:    FETCH_NAV_EQUIPMENT,
                payload: {
                    equipment: [],
                    meta:      {}
                }
            });

            dispatch({
                type:    FETCH_NAV_SYSTEMS,
                payload: {
                    system: [],
                    meta:   {}
                }
            });
            break;
        case 'client':
            dispatch(setNavSite({}));
            dispatch(setNavEquipment({}));
            dispatch(setNavSystem({}));

            dispatch({
                type:    FETCH_NAV_SITES,
                payload: {
                    sites: [],
                    meta:  {}
                }
            });

            dispatch({
                type:    FETCH_NAV_EQUIPMENT,
                payload: {
                    equipment: [],
                    meta:      {}
                }
            });

            dispatch({
                type:    FETCH_NAV_SYSTEMS,
                payload: {
                    system: [],
                    meta:   {}
                }
            });
            break;
        case 'site':
            dispatch(setNavEquipment({}));
            dispatch(setNavSystem({}));

            dispatch({
                type:    FETCH_NAV_EQUIPMENT,
                payload: {
                    equipment: [],
                    meta:      {}
                }
            });

            dispatch({
                type:    FETCH_NAV_SYSTEMS,
                payload: {
                    system: [],
                    meta:   {}
                }
            });
            break;
        case 'navigation':
            dispatch(setNavClient({}));
            dispatch(setNavSite({}));
            dispatch(setNavEquipment({}));

            dispatch(setActiveNavClient({}));
            dispatch(setActiveNavSite({}));
            dispatch(setActiveNavEquipment({}));

            dispatch({
                type:    FETCH_NAV_SITES,
                payload: {
                    sites: [],
                    meta:  {}
                }
            });

            dispatch({
                type:    FETCH_NAV_EQUIPMENT,
                payload: {
                    equipment: [],
                    meta:      {}
                }
            });
            break;
        default:
            break;
    }
};

export const cancelSelectedNavOption = target => async dispatch => {
    switch (target) {
        case 'client':
            dispatch({
                type: CLEAR_SELECTED_NAV_CLIENT
            });
            dispatch({
                type: CLEAR_SELECTED_NAV_SITE
            });
            dispatch({
                type: CLEAR_SELECTED_NAV_EQUIPMENT
            });
            dispatch({
                type: CLEAR_SELECTED_NAV_SYSTEM
            });
            break;
        case 'site':
            dispatch({
                type: CLEAR_SELECTED_NAV_SITE
            });
            dispatch({
                type: CLEAR_SELECTED_NAV_EQUIPMENT
            });
            dispatch({
                type: CLEAR_SELECTED_NAV_SYSTEM
            });
            break;
        case 'equipment':
            dispatch({
                type: CLEAR_SELECTED_NAV_EQUIPMENT
            });
            break;
        case 'system':
            dispatch({
                type: CLEAR_SELECTED_NAV_SYSTEM
            });
            break;
        default:
            break;
    }
};

export const setActiveNavClient = payload => async dispatch => {
    dispatch({
        type: SET_ACTIVE_NAV_CLIENT,
        payload
    });
};

export const setActiveNavCompany = payload => async dispatch => {
    dispatch({
        type: SET_ACTIVE_NAV_COMPANY,
        payload
    });
};

export const setActiveNavEquipment = payload => async dispatch => {
    dispatch({
        type: SET_ACTIVE_NAV_EQUIPMENT,
        payload
    });
};

export const setActiveNavSite = payload => async dispatch => {
    dispatch({
        type: SET_ACTIVE_NAV_SITE,
        payload
    });
};

export const setNavClient = payload => async dispatch => {
    dispatch({
        type: SET_NAV_CLIENT,
        payload
    });
};

export const setNavCompany = payload => async dispatch => {
    dispatch({
        type: SET_NAV_COMPANY,
        payload
    });
};

export const setNavEquipment = payload => async dispatch => {
    dispatch({
        type: SET_NAV_EQUIPMENT,
        payload
    });
};

export const setNavSystem = payload => async dispatch => {
    dispatch({
        type: SET_NAV_SYSTEM,
        payload
    });
};

export const setNavSite = payload => async dispatch => {
    dispatch({
        type: SET_NAV_SITE,
        payload
    });
};

export const setAllSensors = (sensors, meta) => async dispatch => {
    dispatch({
        type:    SET_ALL_SENSORS,
        payload: {
            sensors,
            meta
        }
    });
};

export const fetchEntityRules = (companyId, entityId, StartTime, EndTime, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    options.limit || (options.limit = DEFAULT_GRAPH_PAGE_LIMIT);
    options.page || (options.page = 1);

    // dont pass timestamp when current is selected
    if (getState().dashboard.dashboardIsCurrentTimeStamp) {
        StartTime = null;
        EndTime = null;
    }

    dispatch(setBobLoading('EntityRules'));

    try {
        // Fetches rules data
        const rulesResponse = await k2aBaseApi.get(
            EntityFaultsDataUrl.expand({
                companyId,
                entityId,
                StartTime,
                EndTime,
                ...options
            }),
            header
        );

        // Extract Rules/Faults data from response
        const faults = get(rulesResponse, 'data.equipmentRuleSummaries', {}); 
        const meta = get(rulesResponse, 'data.summaryMeta', {});

        //Dispatches rules data
        dispatch({
            type:    FETCH_EQUIPMENT_RULES,
            payload: {
                faults,
                meta
            }
        });
        
        dispatch(setDashboardCurrentTimestamp(moment().local()));

    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('EntityRules'));
    }
};

export const fetchFaultSummary = (companyId, entityId, StartTime, EndTime, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    options.limit || (options.limit = DEFAULT_GRAPH_PAGE_LIMIT);
    options.page || (options.page = 1);

    // dont pass timestamp when current is selected
    if (getState().dashboard.dashboardIsCurrentTimeStamp) {
        StartTime = null;
        EndTime = null;
    }

    dispatch(setBobLoading('EntityRules'));

    try {
        const response = await k2aBaseApi.get(
            FaultSummaryUrl.expand({
                companyId,
                entityId,
                StartTime,
                EndTime,
                ...options
            }),
            header
        );

        const payload = get(response, 'data.equipmentRuleSummaries', []);
        const meta = get(response, 'data.summaryMeta', {});

        payload.forEach(item => {
            delete item.faultHistory;
            item.issueTypesList = item.issueTypes.join(', ');
        });

        dispatch(setDashboardCurrentTimestamp(moment().local()));

        dispatch({
            type:    FETCH_FAULT_SUMMARY,
            payload: {
                data: payload,
                meta
            }
        });

    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('EntityRules'));
    }
};

export const setFaultSummary = (summary, meta) => async (dispatch, getState) => {
    dispatch({
        type:    FETCH_FAULT_SUMMARY,
        payload: {
            data: summary,
            meta
        }
    });
};

export const fetchFaultHistory = (companyId, entityId, StartTime, EndTime, index, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    options.limit || (options.limit = DEFAULT_GRAPH_PAGE_LIMIT);
    options.page || (options.page = 1);

    //Check if fault history exists before fetching new data
    const faultData = getState().bob.faultData;
    if (faultData[index]) {
        dispatch({
            type:    FETCH_FAULT_HISTORY,
            payload: {
                data: faultData[index].payload,
                meta: faultData[index].meta
            }
        });
        return;
    }


    // dont pass timestamp when current is selected
    if (getState().dashboard.dashboardIsCurrentTimeStamp) {
        StartTime = null;
        EndTime = null;
    }

    dispatch(setBobLoading('EntityRules'));

    try {
        const response = await k2aBaseApi.get(
            FaultHistoryUrl.expand({
                companyId,
                entityId,
                StartTime,
                EndTime,
                ...options
            }),
            header
        );

        const payload = get(response, 'data.equipmentRuleSummaries', []);
        const meta = get(response, 'data.summaryMeta', {});

        dispatch({
            type:    FETCH_FAULT_HISTORY,
            payload: {
                data: payload,
                meta
            }
        });

        const data = { payload, meta };

        dispatch(setFaultData(data, index));
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('EntityRules'));
    }
};

export const fetchFaultOverlay = (companyId, entityId, RuleId, StartTime, EndTime, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('FetchFaultOverlay'));

    try {
        const response = await k2aBaseApi.get(
            FaultHistoryUrl.expand({
                companyId,
                entityId,
                RuleId,
                StartTime,
                EndTime,
                ...options
            }),
            header
        );

        const payload = get(response, 'data.equipmentRuleSummaries', []);
        let data = [];

        if (!isEmpty(payload)) {
            data = payload[0].faultHistory;
        }

        dispatch({
            type:    FETCH_FAULT_OVERLAY,
            payload: data
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('FetchFaultOverlay'));
    }
};

export const setFaultOverlay = payload => async (dispatch, getState) => {
    dispatch({
        type: FETCH_FAULT_OVERLAY,
        payload
    });
};

export const setFaultHistory = (history, meta) => async (dispatch, getState) => {
    dispatch({
        type:    FETCH_FAULT_HISTORY,
        payload: {
            data: history,
            meta
        }
    });
};

export const setFaultData = (faultData, index) => async (dispatch, getState) => {
    let updatedFaultData = getState().bob.faultData;

    if (!isEmpty(faultData)) {
        updatedFaultData[index] = faultData;
        dispatch({
            type:    SET_FAULT_DATA,
            payload: updatedFaultData
        });
    } else {
        dispatch({
            type:    SET_FAULT_DATA,
            payload: []
        });
    }
};

const getRuleFvoSensorFilterName = (meta, ruleId, sensorFilterId) => {
    const rule = meta.info.rules.find(r => r.id === ruleId);
    if (rule) {
        const sensorFilter = rule.sensorFilters.find(sf => sf.id === sensorFilterId);
        if (sensorFilter) {
            return sensorFilter.sensorTypeName ? sensorFilter.sensorTypeName : 'Unknown Sensor Filter';
        }
        else {
            return 'Unknown Sensor Filter';
        }
    }
    else {
        return '';
    }
};

export const fetchScoringData = (companyId, Sensors, Rules, From, To, DesiredTimeSeriesIntervalInMinutes, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    const current = getState().dashboard.dashboardCurrentTimeStamp;
    let sortedPayload = [];
    let totalPayload = [];
    let totalCount = 0;

    dispatch(setBobLoading('ScoreData'));
    dispatch({
        type:    FETCH_SCORING_DATA,
        payload: {data: [], meta: {}}
    });

    // dont pass timestamp when current is selected
    if (getState().dashboard.dashboardIsCurrentTimeStamp) {
        From = null;
        To = null;
    }

    try {
        let meta = {};
        const getPayload = async (page = 1) => {
            options.page = page;
            let url = ScoringDataUrl.expand({ companyId, From, To, DesiredTimeSeriesIntervalInMinutes, Page: page, Limit: 3000, ...options });

            Sensors.forEach(sensor => {
                url = `${url}&SensorId=${sensor.id}`;
            });

            Rules.forEach(rule => {
                url = `${url}&RuleId=${rule}`;
            });

            if (!isEmpty(options.weatherSensors)) {
                options.weatherSensors = options.weatherSensors.replace('maxTemperature', 'tempmax');
                options.weatherSensors = options.weatherSensors.replace('temperature', 'temp');
                options.weatherSensors = options.weatherSensors.replace('minTemperature', 'tempmin');

                url = `${url}&weatherId=${options.weatherSensors}`;
            }

            const scoringResponse = await k2aBaseApi.get(
                url,
                header
            );

            const payload = get(scoringResponse, 'data.data', '');
            meta = get(scoringResponse, 'data.meta', '');
            totalCount = meta.totalCount;
            if (!isNil(payload)) {
                payload.forEach(item => {
                    if (getState().dashboard.dashboardIsCurrentTimeStamp) {
                        item.start = current;
                    }
                    item.start = new Date(item.start);
                    const keys = Object.keys(item);
                    keys.forEach(key => {
                        if (item[key]?.toString().toLowerCase() === 'pollfailure') {
                            item[key] = 'Poll Failure';
                        }
                    });
                    
                    if (item.ruleFvoVariables) {
                        const ruleName = Object.keys(item).find(k => k.includes('ruleName'));
                        const ruleId = ruleName.split('_')[1];
                        const fvoVariables = item.ruleFvoVariables.match(/([^[\]]+|\[\])/g).map( function(val) { return val==='[]' ? null : val; });
                        fvoVariables.filter(v => v.trim() !== ',').forEach(fvo => {
                            const val = fvo.split(',');
                            item[`ruleFvoSensorName_${val[0].trim()}`] = getRuleFvoSensorFilterName(meta, ruleId, val[0].trim());
                            let value = val[1].trim();
                            if (value.toLowerCase() === 'true' || value.toLowerCase() === 'false') {
                                value = value.toLowerCase() === 'true';
                            }
                            else if (!isNaN(value)) {
                                value = toNumber(value);
                            }
                            item[`ruleFvoSensorVal_${val[0].trim()}`] = value;
                        });
                    }
                    else {
                        // build the columns from the meta
                        if (!isEmpty(meta.info.rules)) {
                            meta.info.rules[0].sensorFilters.forEach(sf => {
                                item[`ruleFvoSensorName_${sf.id}`] = sf.sensorTypeName ? sf.sensorTypeName : 'Unknown Sensor Filter';
                                item[`ruleFvoSensorVal_${sf.id}`] = null;
                            });
                        }
                    }
                });
                totalPayload = [...totalPayload, ...payload];

                page++;
                if (payload.length > 0 && totalPayload.length < totalCount) {
                    return await (getPayload(page));
                }
            }
        };

        await getPayload();

        sortedPayload = totalPayload.sort((a, b) => a.start.valueOf() - b.start.valueOf());

        dispatch({
            type:    FETCH_SCORING_DATA,
            payload: {data: sortedPayload, meta: meta}
        });
        
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('ScoreData'));
    }
};

export const setScoreSensorData = data => async (dispatch, getState) => {
    dispatch({
        type:    FETCH_SCORING_DATA,
        payload: {data: data.data, meta: data.meta}
    });
};

export const fetchEntityRulesAndScores = (companyId, entityId, StartTime, EndTime, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    options.limit || (options.limit = DEFAULT_GRAPH_PAGE_LIMIT);
    options.page || (options.page = 1);
    const pageSize = DEFAULT_SCORES_PAGE_LIMIT;

    dispatch(setBobLoading('ScoreData'));

    try {
        dispatch(setProgressBarLoading(true));
        // Fetches rules data
        const rulesResponse = await k2aBaseApi.get(
            EntityFaultsDataUrl.expand({
                companyId,
                entityId,
                StartTime,
                EndTime,
                ...options
            }),
            header
        );

        // Extract Rules/Faults data from response
        const faults = get(rulesResponse, 'data.equipmentRuleSummaries', {});

        // Extract and format the rule scores to feed to scores chart
        const faultScores = [];

        faults.forEach(fault => {
            const item = {
                name: fault.ruleName,
                data: []
            };

            fault.faultHistory.forEach(point => {
                item.data.push({
                    timestamp: point.startTime,
                    value:     point.score * 100
                });
                item.data.push({
                    timestamp: point.endTime,
                    value:     point.score * 100
                });
            });
            faultScores.push(item);
        });

        const startTimestamp = StartTime;
        const endTimestamp = EndTime;

        // Fetch score data for entity
        const scoreResponse = await lambdaBaseApi.get(
            EntityScoresDataUrl.expand({
                entityId,
                startTimestamp,
                endTimestamp,
                pageSize,
                ...options
            }),
            header
        );

        // Extract and sort scores data by date time
        let scores = get(scoreResponse, 'data.data', []) || [];
        // scores.sort((a, b) => a.sensorDataStart - b.sensorDataStart);

        // Initialize overall score object and populate it
        const overallScore = [
            {
                name: 'Overall Score',
                data: []
            }
        ];

        scores.forEach(score => {
            overallScore[0].data.push({
                timestamp: new Date(score.sensorDataStart),
                value:     score.score * 100
            });
        });

        // Concat rules scores and overall score
        const clonedOverallArray = cloneDeep(overallScore);
        const clonedFaultArray = cloneDeep(faultScores);

        const totalScores = [...clonedOverallArray, ...clonedFaultArray];

        const meta = get(rulesResponse, 'data.summaryMeta', {});

        //Dispatches rules data
        dispatch({
            type:    FETCH_EQUIPMENT_RULES,
            payload: {
                faults,
                meta
            }
        });

        // Dispatch total scores array
        dispatch(setEntityScores(totalScores));
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('ScoreData'));
        dispatch(setProgressBarLoading(false));
    }
};

export const setEntitySensors = (sensors, meta) => (dispatch, getState) => {
    dispatch({
        type:    SET_ENTITY_SENSORS,
        payload: {
            sensors,
            meta
        }
    });
};

export const clearEntityScoresData = () => (dispatch, getState) => {
    dispatch({
        type:    SET_ENTITY_SCORES_DATA,
        payload: []
    });
};

export const setEntityRules = (data, meta) => (dispatch, getState) => {
    dispatch({
        type:    SET_ENTITY_RULES,
        payload: {
            data,
            meta
        }
    });
};

export const setEntityScoringData = data => (dispatch, getState) => {
    dispatch({
        type:    SET_ENTITY_SCORING_DATA,
        payload: {data: [], meta: {}}
    });
};

export const setEntityScores = (sensors) => async dispatch => {
    dispatch({
        type:    SET_ENTITY_SCORES_DATA,
        payload: sensors
    });
};

export const setConfigSensors = sensors => async dispatch => {
    dispatch({
        type:    SET_CONFIG_SENSORS,
        payload: sensors
    });
};

export const setCompanyApplications = (companyId) => async (dispatch, getState) => {
    const companies = getState().core.companies;
    const selectedCompany = companies.find(company => company.company.id === companyId);
    const applications = get(selectedCompany, 'applications', []);
    const selectedApplications = applications.map(application => application.applicationName);

    dispatch({
        type:    SET_COMPANY_APPLICATIONS,
        payload: selectedApplications
    });
};
export const setBobLoading = isLoading => async (dispatch, getState) => {
    let loadingArray = [...getState().bob.bobLoading];

    if (loadingArray.includes(isLoading)) {
        loadingArray = loadingArray.filter(item => item !== isLoading);
    } else {
        loadingArray.push(isLoading);
    }

    dispatch({
        type:    SET_BOB_LOADING,
        payload: [...loadingArray]
    });
};

export const fetchAllEntitySensors = (siteId, equipments, systems, activeTab, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    let entities = [...equipments,  ...systems];

    if (options.mutateCompanyId) {
        const mutatedCompanyId = getState().bob.targetNavCompany.id;

        header.headers['K2A-Company'] = mutatedCompanyId;
    }
    
    options.limit || (options.limit = 500);
    options.page || (options.page = 1);
    options.searchFilter || (options.searchFilter = '');
    options.sort || (options.sort = DEFAULT_SORT);

    dispatch(setBobLoading('fetchAllEntitySensors'));

    try {
        const manager = ConcurrencyManager(lambdaBaseApi, MAX_CONCURRENCY_REQUESTS);

        let promises = [];
        entities.forEach(item => {
            promises.push(
                k2aBaseApi.get(
                    EquipmentSensorDataUrl.expand({
                        siteId,
                        parentId: item.id,
                        ...options
                    }),
                    header
                )
            );
        });

        let results = [];
        const rawUnits = getState().architect.rawUnits;

        await Promise.all(promises.map(p => p))
            .then(axios.spread((...result) => {
                result.forEach(r => {
                    r.data.data.forEach(s => {
                        if (!isNil(s.linkedSource.agentName) && !isNil(s.linkedSource.deviceName) && !isNil(s.linkedSource.deviceObjectName)) {
                            s.linkedSourceText = s.linkedSource.agentName + '/' + s.linkedSource.deviceName + '/' + s.linkedSource.deviceObjectName;
                        }
                        else if (s.calculatedSensor) {
                            s.linkedSourceText = s.sensorTemplateName;
                        }
                        else {
                            s.linkedSourceText = 'Not linked';
                        }
                        const rawUnit = rawUnits.flatMap(u => u.items).find(ru => ru.id === s.rawUnitId);
                        if (rawUnit) {
                            s.rawUnitDisplay = rawUnit.displayValue;
                        }
                        else {
                            s.rawUnitDisplay = '-';
                        }
                        // this field is used in listbox
                        s.available = true;
                    });

                    const data = get(r, 'data.data', []);
                    results.push(data);
                });
            }));
            
        manager.detach();
        
        entities.forEach((item, id) => {
            item.sensors = results[id];
        });
        
        entities.forEach(item => {
            item.sensors.forEach(i => {
                // id's are different so using name
                let tempSensor = activeTab.sensors.find(s => s.name === i.name);
                i.available = tempSensor && tempSensor.available === false ? false : true;
                if (!i.available) {
                    if (!isEmpty(tempSensor.conditionalFormats)) {
                        i.conditionalFormats = tempSensor.conditionalFormats;
                        i.selected = tempSensor.selected;
                    }
                }
            });
        });

        return entities;
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('fetchAllEntitySensors'));
    }
};
